import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Signature, DownloadSimple } from "@phosphor-icons/react";

export default function WaiversPanel() {
  const [settings, setSettings] = useState(null);
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);
  const [sigs, setSigs] = useState([]);
  const [preview, setPreview] = useState(null);

  const load = async () => {
    try {
      const [s, l] = await Promise.all([api.get("/admin/waiver"), api.get("/admin/waiver-signatures")]);
      setSettings(s.data); setText(s.data.text); setSigs(l.data.signatures || []);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/waiver", { text });
      toast.success(`Waiver saved — now version ${data.version}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const exportCsv = () => {
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const rows = [["Name", "Email", "Phone", "Signed at (UTC)", "Waiver version", "Media consent", "Signature type", "Source", "Ref", "IP"]]
      .concat(sigs.map((s) => [s.name, s.email, s.phone, s.at, s.version, s.media_consent ? "YES" : "NO", s.signature_type, s.source, s.ref, s.ip]));
    const csv = rows.map((r) => r.map(esc).join(",")).join("\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    a.download = `waiver-signatures-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
  };

  if (!settings) return <div className="text-sm text-[#000000]">Loading…</div>;
  return (
    <div data-testid="waivers-panel">
      <div className="rounded-xl border border-[#C4C8CE] bg-white p-4 mb-5">
        <div className="flex items-center gap-2 font-display font-bold mb-1">
          <Signature size={18} weight="duotone" color="#84CC16" /> Waiver text
          <span className="text-xs font-sans font-normal text-[#000000]">version {settings.version} — editing bumps the version; new signers always sign the latest</span>
        </div>
        <textarea value={text} onChange={(e) => setText(e.target.value)} rows={14}
          className="w-full rounded-lg border border-[#C4C8CE] p-3 text-xs font-mono leading-relaxed" data-testid="waiver-text-editor" />
        <button className="btn-primary mt-2" onClick={save} disabled={saving} data-testid="waiver-save-button">
          {saving ? "Saving…" : "Save waiver"}
        </button>
      </div>

      <div className="rounded-xl border border-[#C4C8CE] bg-white p-4">
        <div className="flex items-center justify-between mb-2">
          <div className="font-display font-bold">Signed waivers <span className="text-xs font-sans font-normal text-[#000000]">({sigs.length})</span></div>
          {sigs.length > 0 && (
            <button className="btn-secondary text-xs flex items-center gap-1" onClick={exportCsv} data-testid="waiver-export-csv">
              <DownloadSimple size={14} /> Export CSV
            </button>
          )}
        </div>
        {sigs.length === 0 ? (
          <div className="text-sm text-[#000000]">No signatures yet — they appear here when someone books a session or signs up for an event.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs uppercase tracking-wide text-[#000000]">
                <th className="py-1 pr-3">Name</th><th className="py-1 pr-3">Contact</th><th className="py-1 pr-3">Signed</th>
                <th className="py-1 pr-3">Ver</th><th className="py-1 pr-3">Media</th><th className="py-1 pr-3">Source</th><th className="py-1">Signature</th>
              </tr></thead>
              <tbody>
                {sigs.map((s) => (
                  <tr key={s.id} className="border-t border-[#E4E4E7]" data-testid={`waiver-row-${s.id}`}>
                    <td className="py-1.5 pr-3 font-semibold">{s.name}</td>
                    <td className="py-1.5 pr-3 text-xs text-[#000000]">{s.email}{s.email && s.phone ? " · " : ""}{s.phone}</td>
                    <td className="py-1.5 pr-3 text-xs">{new Date(s.at).toLocaleString()}</td>
                    <td className="py-1.5 pr-3">v{s.version}</td>
                    <td className="py-1.5 pr-3">{s.media_consent ? <span className="text-[#4d7c0f] font-bold">Yes</span> : <span className="text-[#B91C1C] font-bold">No</span>}</td>
                    <td className="py-1.5 pr-3 text-xs">{s.source}{s.ref ? ` · ${s.ref}` : ""}</td>
                    <td className="py-1.5">
                      {s.signature_type === "drawn"
                        ? <button className="text-xs underline text-[#2563EB] bg-transparent border-0 cursor-pointer" onClick={() => setPreview(s)} data-testid={`waiver-view-sig-${s.id}`}>view</button>
                        : <span className="text-xs italic" style={{ fontFamily: "'Brush Script MT','Segoe Script',cursive", fontSize: 16 }}>{s.signature}</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {preview && (
        <div className="fixed inset-0 z-[90] bg-black/60 grid place-items-center p-4" onClick={() => setPreview(null)} data-testid="waiver-signature-preview">
          <div className="bg-white rounded-xl p-4 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="font-bold mb-1">{preview.name} — {new Date(preview.at).toLocaleString()}</div>
            <img src={preview.signature} alt="signature" className="w-full border border-[#E4E4E7] rounded bg-white" />
            <button className="btn-secondary mt-2 w-full" onClick={() => setPreview(null)}>Close</button>
          </div>
        </div>
      )}
    </div>
  );
}
